import type {Process} from "../../types";

// Processes — top-level, many-to-many with FSAs
export const PROCESSES: Process[] = [
  {
    id: "proc-vendor-invoice",
    name: "Vendor invoice processing & three-way match",
    description:
      "End-to-end handling of vendor invoices from receipt through general ledger posting.\n\n**Key steps:**\n- **Receipt & validation** — invoices matched against purchase orders and goods receipts\n- **Approval routing** — routed based on amount thresholds and cost centre\n- **Three-way match** — PO, goods receipt, and invoice must reconcile before payment\n- **GL posting** — approved invoices posted with proper account coding",
    fsaIds: ["expenditures", "revenue"],
    diagramId: "hardcoded-p2p-001",
  },
  {
    id: "proc-expense-reimbursement",
    name: "Employee expense reimbursement",
    description:
      "Submission, approval, and payment of employee-incurred business expenses.\n\n**Expense categories covered:**\n- Travel & accommodation\n- Meals & entertainment\n- Office supplies & equipment\n\n**Controls:** Manager approval required; policy compliance review for amounts exceeding thresholds.",
    fsaIds: ["expenditures"],
  },
  {
    id: "proc-corp-card",
    name: "Corporate credit card reconciliation",
    description:
      "Monthly reconciliation of corporate credit card activity.\n\n**Process:**\n1. Statement downloaded and distributed to cardholders\n2. Cardholders submit expense reports with receipts\n3. Cost centre allocation and coding review\n4. Exception identification and resolution\n5. Final reconciliation sign-off by Finance",
    fsaIds: ["expenditures"],
  },
  {
    id: "proc-order-fulfillment",
    name: "Customer order fulfillment & billing",
    description:
      "Processing of customer orders from entry through invoicing.\n\n**Stages:**\n- **Order entry** — sales order created and validated\n- **Warehouse** — pick, pack, and ship with delivery confirmation\n- **Billing** — automated invoice generation upon shipment\n- **Revenue posting** — recognised per ASC 606 criteria",
    fsaIds: ["revenue"],
    diagramId: "hardcoded-o2c-001",
  },
  {
    id: "proc-contract-rev-rec",
    name: "Contract revenue recognition",
    description:
      "Revenue recognition for multi-element arrangements under **ASC 606**.\n\n**Key activities:**\n- Identification of **performance obligations** within contracts\n- Allocation of **transaction price** to each obligation\n- Determination of recognition timing (point-in-time vs. over time)\n- Periodic reassessment of variable consideration estimates",
    fsaIds: ["revenue"],
  },
  {
    id: "proc-credit-collections",
    name: "Customer credit & collections",
    description:
      "Management of customer credit risk and receivables collection.\n\n**Procedures:**\n- **Credit approval** — limit establishment and annual review\n- **Aging analysis** — weekly review of outstanding balances\n- **Dunning** — automated notices at 30, 60, and 90 days\n- **Write-off** — bad debt approval requires Controller sign-off",
    fsaIds: ["revenue"],
  },
  {
    id: "proc-journal-entry",
    name: "Journal entry processing",
    description:
      "Preparation, review, and posting of journal entries.\n\n**Entry types:** Manual adjustments, automated recurring entries, and intercompany allocations.\n\n**Controls:**\n- Supporting documentation attached to every entry\n- **Dual approval** required for entries above materiality threshold\n- Segregation of duties between preparer and approver\n- Automated validation of account code combinations",
    fsaIds: ["financial-reporting"],
    diagramId: "hardcoded-icfr-001",
  },
  {
    id: "proc-month-end-close",
    name: "Month-end close & reconciliation",
    description:
      "Structured close process with defined calendar and task ownership.\n\n**Close activities:**\n1. Sub-ledger reconciliation preparation and review\n2. Flux analysis of significant balance sheet and P&L movements\n3. Accrual and reserve adjustments\n4. Management review and sign-off\n\n**Timeline:** Close completed within **5 business days** of month-end.",
    fsaIds: ["financial-reporting"],
  },
  {
    id: "proc-intercompany",
    name: "Intercompany eliminations",
    description:
      "Elimination of intercompany transactions and balances during consolidation.\n\n**Process steps:**\n- **Identification** — flag intercompany activity across legal entities\n- **Matching** — reconcile offsetting entries between entities\n- **Dispute resolution** — investigate and resolve unmatched items\n- **Elimination entries** — post consolidating adjustments in the reporting system",
    fsaIds: ["financial-reporting"],
  },
];
